import {
  CIRCLE_DOMAIN,
  GDPR_COOKIE_NAME,
} from "@circle-react/components/constants";
import { staticPages } from "@circle-react/helpers/urlHelpers";
import { Button } from "@circleco/compass/components/Button";
import { Link } from "@circleco/compass/components/Link";
import { Typography } from "@circleco/compass/components/Typography";
import { useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { createPortal } from "react-dom";

import { t } from "@/i18n-js/instance";
import { isProductionEnv, isStagingEnv } from "@/react/helpers/envHelpers";

import { getExpirationDate } from "./helpers";
import { REQUIRED_COUNTRIES } from "./requiredCountries";

const hasExcludedQueryParam = (): boolean => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get("request_host") === "app.circle.so";
};

const excludedPaths = [
  "/sign_up",
  "/communities/new",
  /^\/communities\/\d+\/templates$/,
  /^\/communities\/\d+\/questions$/,
  /^\/live\//,
];

const isDeployedEnv = () => isProductionEnv() || isStagingEnv();

const isOnboardingPath = (): boolean => {
  const currentPath = window.location.pathname;
  return excludedPaths.some(pattern => {
    if (typeof pattern === "string") {
      return currentPath === pattern;
    }
    return pattern.test(currentPath);
  });
};

export const GDPRCookieBannerComponent = () => {
  const isInsideMobileWebView: boolean =
    !!window.webview?.isComingFromReactNativeWebview ||
    !!window.webview?.isInsideMobileWebview;
  const [cookies, setCookie] = useCookies([GDPR_COOKIE_NAME]);
  const [shouldHideBanner, setShouldHideBanner] = useState<boolean>(true);
  const [countryCode, setCountryCode] = useState<string | undefined>(
    window.CLOUDFLARE_COUNTRY_CODE,
  );
  const { [GDPR_COOKIE_NAME]: acceptedCookies } = cookies;

  const getCurrentUrlDomain = (): string => {
    const hostname = window.location.hostname;
    // Check if the hostname matches the pattern something.circle.so
    const isSubdomainOfCircle =
      hostname.endsWith(`.circle.so`) && hostname.split(".").length === 3;

    if (isSubdomainOfCircle) {
      return CIRCLE_DOMAIN;
    }

    return hostname;
  };

  const isSecureCookie = (): boolean => isDeployedEnv();

  const shouldShowBanner = (): boolean => isDeployedEnv();

  const handleClick = (accepted: boolean): void => {
    setCookie(GDPR_COOKIE_NAME, accepted, {
      expires: getExpirationDate(),
      domain: getCurrentUrlDomain(),
      path: "/",
      sameSite: "none",
      secure: isSecureCookie(),
    });
  };

  const getCountryCode = () => {
    if (!isDeployedEnv()) return;
    if (!acceptedCookies && !countryCode?.length) {
      fetch("https://cloudflare-quic.com/b/headers")
        .then(res => res.json())
        .then((data: { headers: { "Cf-Ipcountry": string } }) =>
          setCountryCode(data.headers["Cf-Ipcountry"]),
        )
        .catch((error: unknown) => {
          console.error(error);
        });
    }
  };

  const setBannerIfRequiredCountry = () => {
    const countryCodes = Object.keys(REQUIRED_COUNTRIES);
    setShouldHideBanner(!countryCodes.includes(countryCode || ""));
  };

  useEffect(() => {
    getCountryCode();
  });

  useEffect(() => {
    setBannerIfRequiredCountry();
  }, [countryCode]);

  // cookies_enabled is now HttpOnly so JS cannot read it directly;
  // navigator.cookieEnabled checks first-party cookie support at the browser level.
  if (
    !navigator.cookieEnabled ||
    shouldHideBanner ||
    acceptedCookies !== undefined ||
    isInsideMobileWebView ||
    hasExcludedQueryParam() ||
    isOnboardingPath()
  ) {
    return null;
  }

  if (!shouldShowBanner()) {
    return null;
  }

  return createPortal(
    <div
      id="circle-cookie-banner"
      className="border-primary bg-primary fixed bottom-0 z-[999999] w-full gap-4 border py-3 px-4 shadow-lg md:bottom-8 md:right-8 md:flex md:w-auto md:max-w-[calc(100%-4rem)] md:items-center md:rounded-xl"
    >
      <div className="mb-4 leading-4 md:mb-0 md:flex-1">
        <Typography variant="body-sm" color="primary">
          {t(["gdpr.cookie_banner.description"])}{" "}
          {t("gdpr.cookie_banner.privacy_policy.prefix").trim()}{" "}
          <Link
            href={staticPages.privacy_policy()}
            target="_blank"
            rel="noreferrer"
            variant="custom"
            typography="body-sm"
            color="primary"
          >
            <span className="underline">
              {t("gdpr.cookie_banner.privacy_policy.link")}
            </span>
          </Link>{" "}
          {t("gdpr.cookie_banner.privacy_policy.suffix").trim()}
        </Typography>
      </div>
      <div className="flex flex-row items-center gap-4">
        <div className="flex-1 md:flex-none">
          <Button
            onClick={() => handleClick(false)}
            variant="secondary"
            size="sm"
            fullWidth
            type="button"
          >
            {t("gdpr.cookie_banner.decline_button")}
          </Button>
        </div>
        <div className="flex-1 md:flex-none">
          <Button
            onClick={() => handleClick(true)}
            variant="primary"
            size="sm"
            fullWidth
            type="button"
          >
            {t("gdpr.cookie_banner.accept_button")}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
